import { LRUCache } from 'lru-cache'
import { hash as ohash } from 'ohash'
import { XMLParser } from 'fast-xml-parser'
import { maxContentSize } from '~/constants'
import type { Content, ContentType, PageResult } from '~/types'

const apiBaseUrl = 'https://content.bt.co/channels'

const defaultSizeLimit = 20

const promiseCache = new LRUCache<string, any>({
  max: 500,
  ttl: 10 * 1000 * 60, // 10 min
})

// DOMParser is browser-only, so it breaks server-side rendering and prerender.
// fast-xml-parser works in both environments.
const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  cdataPropName: '__cdata',
  trimValues: true,
})

function cdataText(node: any): string {
  return (node?.__cdata ?? node?.['#text'] ?? node ?? '').toString().trim()
}

function parseContent(xml: string, type: ContentType, id: string): Content {
  const inLine = xmlParser.parse(xml)?.VAST?.Ad?.InLine
  const creative = inLine?.Creatives?.Creative
  const linear = creative?.Linear

  return {
    fetchId: id,
    id: creative?.['@_id'] ?? '',
    title: inLine?.AdTitle ?? '',
    description: inLine?.Description ?? '',
    duration: linear?.Duration ?? '',
    thumbnail: cdataText(linear?.Thumbnails?.Thumbnail),
    type,
    mediaSrc: cdataText(linear?.MediaFiles?.MediaFile),
  }
}

async function _requestContent(type: ContentType, id: string): Promise<string> {
  return await $fetch(`${type}/content-${id}.xml`, {
    baseURL: apiBaseUrl,
  })
}

export async function requestContent(type: ContentType, id: string): Promise<Content> {
  const hash = ohash([type, id])
  const state = useState<Content | null>(hash, () => null)

  if (state.value)
    return state.value

  if (!promiseCache.has(hash)) {
    try {
      const data = await _requestContent(type, id)
      const content = parseContent(data, type, id)

      state.value = content
      promiseCache.set(hash, Promise.resolve(content))
      return content
    }
    catch (e) {
      promiseCache.delete(hash)
      throw createError(e as any)
    }
  }

  return promiseCache.get(hash)!
}

export async function requestContentNoCache(type: ContentType, id: string) {
  try {
    const data = await _requestContent(type, id)
    return parseContent(data, type, id)
  }
  catch (e) {
    throw createError(e as any)
  }
}

export async function listContentById(type: ContentType, ids: number[]): Promise<PageResult<Content>> {
  let results: Content[] = []
  let total = maxContentSize
  try {
    results = await Promise.all(ids.map(id => requestContentNoCache(type, id.toString())))
  }
  catch (error) {
    results = []
    total = 0
  }

  return {
    results,
    total,
  }
}

export function listContentByPageWrapper(type: ContentType) {
  const total = initRandomIndexArray(maxContentSize)

  return (page: number, size = defaultSizeLimit) => {
    const ids = total.slice((page - 1) * size, page * size)
    return listContentById(type, ids)
  }
}
